import { useState } from "react";
import { Link } from "react-router-dom";
import { User, Mail, Tag, MessageSquare, Loader2, Send, CheckCircle2, ArrowLeft, LifeBuoy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CustomerTypePicker, emptyCustomer, customerError, customerPayload } from "@/components/CustomerTypePicker";

const SUPPORT_EMAIL = "support@smrtin.net";

function Field({ icon: Icon, label, testid, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition"
        />
      </div>
    </div>
  );
}

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [customer, setCustomer] = useState(emptyCustomer);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email and message.");
      return;
    }
    if (customerError(customer)) {
      setError(customerError(customer));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        subject: form.subject.trim() ? `[Support] ${form.subject.trim()}` : "[Support] New request",
        message: form.message.trim(),
        ...customerPayload(customer),
      });
      setSent(true);
      toast.success("Your request has been sent to our support team.");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="support-page">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Link
            to="/login"
            data-testid="support-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition mb-4"
          >
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="relative bg-slate-900 px-6 py-7 overflow-hidden">
              <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-indigo-600/30 blur-2xl" />
              <div className="relative flex items-center gap-3">
                <img src="/logo-lg.webp" alt="Smart-In" width="44" height="44" className="h-11 w-11 rounded-xl object-cover bg-white shadow" />
                <div>
                  <div className="flex items-center gap-1.5 text-indigo-300 text-xs font-bold uppercase tracking-widest">
                    <LifeBuoy className="h-4 w-4" /> Support
                  </div>
                  <h1 className="font-head text-2xl font-extrabold text-white tracking-tight leading-tight">How can we help?</h1>
                </div>
              </div>
              <p className="relative mt-3 text-sm text-slate-300">
                Send us your question or issue and our team will get back to you by email. You can also reach us directly at{" "}
                <a
                  data-testid="support-email-link"
                  href={`mailto:${SUPPORT_EMAIL}?subject=Smart-In%20Support`}
                  className="text-white font-semibold hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>

            {sent ? (
              <div className="px-6 py-12 text-center" data-testid="support-success">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 grid place-items-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h2 className="mt-4 font-head text-xl font-bold text-slate-900">Request received</h2>
                <p className="mt-1 text-sm text-slate-500">Thanks for reaching out — we'll reply to <b>{form.email}</b> shortly.</p>
                <button
                  onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); setCustomer(emptyCustomer); }}
                  data-testid="support-new-request-btn"
                  className="mt-6 h-11 px-6 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 py-6 space-y-4" data-testid="support-form">
                <CustomerTypePicker prefix="support" value={customer} onChange={(c) => { setCustomer(c); setError(""); }} />
                <Field icon={User} label="Your Name" testid="support-name" value={form.name} onChange={set("name")} placeholder="Full name" />
                <Field icon={Mail} label="Email" testid="support-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                <Field icon={Tag} label="Subject (optional)" testid="support-subject" value={form.subject} onChange={set("subject")} placeholder="Brief summary" />
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Question or Issue</label>
                  <textarea
                    data-testid="support-message"
                    value={form.message}
                    onChange={(e) => set("message")(e.target.value)}
                    rows={5}
                    placeholder="Describe your question or the issue you're facing…"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition resize-none"
                  />
                </div>

                {error && (
                  <p className="text-sm font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="support-error">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="support-submit-btn"
                  className="w-full h-12 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Submit Request <Send className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-5">© {new Date().getFullYear()} Smart-In · smrtin.net</p>
        </div>
      </div>
    </div>
  );
}
